import { type Vector2 } from '@canvas/vector-types';

import { type AnyElement, type AnyElementData } from '../all-element-types.ts';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element.ts';

/**
 * Supported file extensions for adding images.
 */
export const supportedImageExtensions = [
  '.png',
  '.jpeg',
  '.webp',
  '.gif',
  '.jpg',
  '.svg',
];

/**
 * Supported file extensions for importing files.
 */
export const supportedFileImportExtensions = ['.pdf'];

export enum FileStatus {
  Pending = 0,
  Uploading = 1,
  Downloading = 2,
  UploadError = 3,
  DownloadError = 4,
  Ready = 5,
  LocalOnly = 6,
}

/**
 * How the image is sized inside its frame, before applying the offset.
 */
export enum ImageFit {
  /**
   * Show the whole image without changing its proportions. May leave empty space.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/object-fit#contain
   */
  Contain = 0,
  /**
   * Fill the frame without changing the image's proportions. Clip any overflow.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/object-fit#cover
   */
  Cover = 1,
  /**
   * Scale width and height separately to fill the frame. May distort the image.
   * Matches CSS object-fit: fill.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/object-fit#fill
   */
  Stretch = 2,
}

export const imageFits = [ImageFit.Contain, ImageFit.Cover, ImageFit.Stretch] as const;

/**
 * The renderer's legacy fit hint. Persisted authored placement uses ImageFit.
 */
export type ImageObjectFit = 'cover' | 'contain';

export interface ImagePlacement {
  /**
   * How the image fits inside the element's frame. When absent, use the
   * application's existing fit behavior. See docs/guides/image-content-placement.md.
   */
  contentFit?: ImageFit;
  /**
   * Move the image from the center by a fraction of the frame width and height.
   * For example, (0.1, -0.25) moves it 10% right and 25% up, before frame rotation.
   * When absent, center the image. Values must be finite but may be outside 0..1.
   * Keep fit and offset unchanged when resizing the frame proportionally.
   */
  contentOffset?: Vector2;
}

export interface ImageElementData extends ElementData<ElementType>, ImagePlacement {
  /** The file ID returned by the media service */
  fileId?: string;
  /** File data */
  blob?: Blob;
  /** URL to file */
  url?: string;

  /**
   * The size of the underlying image data, in physical pixels.
   */
  nativeSize?: Vector2 | null;
  /**
   * The mimetype of the underlying image.
   */
  mimeType?: string;
  /**
   * A SHA-512 hash of the image data, to allow us to detect images with identical contents.
   */
  imageHash?: string;
  /**
   * A boolean indicating if the image contains any transparent pixels.
   */
  isTransparent?: boolean;
  /**
   * Whether this image is a placeholder awaiting user-provided media.
   */
  isPlaceholder?: boolean;
  /**
   * Marks the image as not being included as an individual undo step.
   * This is useful for batched inserts such as templates.
   */
  excludeFromUndoStack?: boolean;
  rotation?: number;

  /**
   * ⚠️ If we ever add basisSize and basisPosition here, ensure that the corresponding type-guards
   * are updated accordingly in @link packages/sizing-feature-flags/src/sizing-feature-flags.ts
   */
}

export interface ImageElement extends ImageElementData, Element<ElementType> {}

export function isImage(element: AnyElement | undefined): element is ImageElement {
  return !!element && element.type === imageElementType;
}

export function isImageData(
  element: AnyElementData | undefined,
): element is ImageElementData {
  return !!element && element.type === imageElementType;
}
export const imageElementType = elementType('image');
